import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ChevronLeft,
	ChevronRight,
	ExternalLink,
	Eye,
	FileImage,
	Film,
	ImageIcon,
	Plus,
	RefreshCw,
	Search,
	ShieldAlert,
	Trash2,
	X,
} from 'lucide-react'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import {
	mediaKeys,
	mediaKindLabel,
	mediaStateLabel,
	terminalMediaStates,
	type MediaAsset,
	type MediaKind,
	type MediaState,
} from '@entities/media'
import { createCommandHeaders, createIdempotencyKey, getNextCursor, unwrapApiResponse } from '@shared/api'
import { getInterfaceErrorDescription } from '@shared/i18n/interface-messages'
import { ConfirmDialog } from '@shared/ui/confirm-dialog'
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@shared/ui/page'
import { useToast } from '@shared/ui/toast'
import { canMarkMediaForDeletion } from '../model/deletion'
import { MediaUploadDialog } from './upload-dialog'

type MediaFilters = {
	search: string
	kind: '' | MediaKind
	state: '' | MediaState
	limit: number
}

const defaultFilters: MediaFilters = { search: '', kind: '', state: '', limit: 25 }
const parseFilters = (search: string): MediaFilters => {
	const params = new URLSearchParams(search)
	const kind = params.get('kind') as MediaKind | null
	const state = params.get('state') as MediaState | null
	const limit = Number(params.get('limit'))
	return {
		search: params.get('search')?.trim() ?? '',
		kind: kind && kind in mediaKindLabel ? kind : '',
		state: state && state in mediaStateLabel ? state : '',
		limit: [10, 25, 50, 100].includes(limit) ? limit : 25,
	}
}
const serializeFilters = (filters: MediaFilters) => {
	const params = new URLSearchParams()
	if (filters.search) params.set('search', filters.search)
	if (filters.kind) params.set('kind', filters.kind)
	if (filters.state) params.set('state', filters.state)
	if (filters.limit !== defaultFilters.limit) params.set('limit', String(filters.limit))
	const value = params.toString()
	return value ? `?${value}` : ''
}

const stateClass: Record<MediaState, string> = {
	CREATED: 'bg-slate-100 text-slate-700',
	UPLOADING: 'bg-blue-100 text-blue-800',
	UPLOADED: 'bg-indigo-100 text-indigo-800',
	PROCESSING: 'bg-violet-100 text-violet-800',
	READY: 'bg-emerald-100 text-emerald-800',
	REJECTED: 'bg-red-100 text-red-800',
	DELETING: 'bg-amber-100 text-amber-900',
	DELETED: 'bg-slate-200 text-slate-600',
}

const formatBytes = (value?: number) => {
	if (!value) return '—'
	if (value < 1024) return `${value} Б`
	if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} КБ`
	if (value < 1024 ** 3) return `${(value / 1024 ** 2).toFixed(1)} МБ`
	return `${(value / 1024 ** 3).toFixed(1)} ГБ`
}
const formatDate = (value?: string) => value
	? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
	: '—'

const AssetPreview = ({ asset }: { asset: MediaAsset }) => {
	const url = asset.variants?.[0]?.url
	if (url && asset.kind !== 'VIDEO') {
		return <img src={url} alt='' className='h-14 w-14 rounded-xl border bg-muted object-cover' />
	}
	const Icon = asset.kind === 'VIDEO' ? Film : asset.kind === 'IMAGE' ? ImageIcon : FileImage
	return <div className='flex h-14 w-14 items-center justify-center rounded-xl border bg-muted/50 text-muted-foreground'><Icon className='h-6 w-6' /></div>
}

export const MediaCatalog = () => {
	const api = useAdminApi()
	const { can } = useAuth()
	const { notify } = useToast()
	const queryClient = useQueryClient()
	const location = useLocation()
	const history = useHistory()
	const filters = useMemo(() => parseFilters(location.search), [location.search])
	const [searchDraft, setSearchDraft] = useState(filters.search)
	const [cursor, setCursor] = useState<string | null>(null)
	const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([])
	const [uploadOpen, setUploadOpen] = useState(false)
	const [deleteAsset, setDeleteAsset] = useState<MediaAsset | null>(null)
	const [detailAsset, setDetailAsset] = useState<MediaAsset | null>(null)
	const [deletePending, setDeletePending] = useState(false)

	useEffect(() => {
		setSearchDraft(filters.search)
		setCursor(null)
		setCursorHistory([])
	}, [filters.search, filters.kind, filters.state, filters.limit])

	const queryFilters = useMemo(() => ({ ...filters, cursor }), [filters, cursor])
	const query = useQuery({
		queryKey: mediaKeys.list(queryFilters),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/media', {
			params: { query: {
				cursor: cursor ?? undefined,
				limit: filters.limit,
				search: filters.search || undefined,
				kind: filters.kind ? [filters.kind] : undefined,
				state: filters.state ? [filters.state] : undefined,
			} },
		})).data,
		placeholderData: previous => previous,
		refetchInterval: current => current.state.data?.items.some(asset => !terminalMediaStates.has(asset.state)) ? 2000 : false,
	})

	const updateFilters = (patch: Partial<MediaFilters>) => {
		const next = { ...filters, ...patch }
		history.replace({ pathname: location.pathname, search: serializeFilters(next) })
	}
	const submitSearch = (event: FormEvent) => {
		event.preventDefault()
		updateFilters({ search: searchDraft.trim() })
	}
	const resetFilters = () => {
		setSearchDraft('')
		history.replace({ pathname: location.pathname, search: '' })
	}
	const goNext = () => {
		const next = getNextCursor(query.data?.page)
		if (!next) return
		setCursorHistory(current => [...current, cursor])
		setCursor(next)
	}
	const goPrevious = () => {
		setCursorHistory(current => {
			if (!current.length) return current
			setCursor(current[current.length - 1])
			return current.slice(0, -1)
		})
	}
	const markDelete = async () => {
		if (!deleteAsset) return
		setDeletePending(true)
		try {
			const result = unwrapApiResponse(await api.DELETE('/admin/v1/media/{asset_id}', {
				params: {
					path: { asset_id: deleteAsset.id },
					header: createCommandHeaders({ idempotencyKey: createIdempotencyKey(), etag: deleteAsset.etag }) as {
						'Idempotency-Key': string
						'If-Match': string
					},
				},
			})).data
			await queryClient.invalidateQueries({ queryKey: mediaKeys.all })
			notify({ title: 'Ассет помечен на удаление', description: `${result.filename}: запущен период отложенного удаления`, tone: 'success' })
			setDeleteAsset(null)
		} catch (error) {
			notify({ title: 'Удаление заблокировано', description: getInterfaceErrorDescription(error), tone: 'error' })
		} finally {
			setDeletePending(false)
		}
	}
	const hasFilters = Boolean(filters.search || filters.kind || filters.state)

	return (
		<section className='space-y-6'>
			<PageHeader
				eyebrow='Asset management'
				title='Медиатека'
				description='Прямая загрузка в объектное хранилище, обработка вариантов, защита связей, контроль версии и отложенное удаление.'
				actions={<div className='flex gap-2'>
					<Button variant='outline' onClick={() => void query.refetch()} disabled={query.isFetching}><RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} /> Обновить</Button>
					{can('createMediaUpload') ? <Button onClick={() => setUploadOpen(true)}><Plus className='h-4 w-4' /> Загрузить</Button> : null}
				</div>}
			/>

			<Card><CardContent className='space-y-4 p-4 sm:p-5'>
				<form onSubmit={submitSearch} className='flex flex-col gap-2 sm:flex-row'>
					<div className='relative flex-1'><Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' /><Input aria-label='Поиск медиафайлов' value={searchDraft} onChange={event => setSearchDraft(event.target.value)} placeholder='Поиск по имени или ключу хранения' className='pl-9' /></div>
					<Button type='submit' variant='outline'>Найти</Button>
					{hasFilters ? <Button type='button' variant='ghost' onClick={resetFilters}><X className='h-4 w-4' /> Сбросить</Button> : null}
				</form>
				<div className='grid gap-3 sm:grid-cols-3'>
					<select value={filters.kind} onChange={event => updateFilters({ kind: event.target.value as MediaFilters['kind'] })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Тип ассета'><option value=''>Все типы</option>{Object.entries(mediaKindLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
					<select value={filters.state} onChange={event => updateFilters({ state: event.target.value as MediaFilters['state'] })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Состояние'><option value=''>Все состояния</option>{Object.entries(mediaStateLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
					<select value={filters.limit} onChange={event => updateFilters({ limit: Number(event.target.value) })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Размер страницы'>{[10, 25, 50, 100].map(value => <option key={value} value={value}>{value} на странице</option>)}</select>
				</div>
			</CardContent></Card>

			{query.isPending ? <LoadingState label='Загрузка медиатеки…' /> : null}
			{query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} title='Сервис медиатеки недоступен' /> : null}
			{query.data && query.data.items.length === 0 ? <EmptyState title='Медиафайлы не найдены' description={hasFilters ? 'Измените поиск или фильтры.' : 'Загрузите первый ассет в медиатеку.'} action={can('createMediaUpload') && !hasFilters ? <Button onClick={() => setUploadOpen(true)}><Plus className='h-4 w-4' /> Загрузить файл</Button> : undefined} /> : null}
			{query.data && query.data.items.length > 0 ? <Card className='overflow-hidden'>
				<div className='overflow-x-auto'><table className='w-full min-w-[980px] text-left text-sm'>
					<caption className='sr-only'>Медиатека</caption>
					<thead className='border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'><tr><th scope='col' className='px-4 py-3'>Ассет</th><th scope='col' className='px-4 py-3'>Состояние</th><th scope='col' className='px-4 py-3'>Размер</th><th scope='col' className='px-4 py-3'>Варианты</th><th scope='col' className='px-4 py-3'>Ссылки</th><th scope='col' className='px-4 py-3'>Изменён</th><th scope='col' className='px-4 py-3 text-right'>Действия</th></tr></thead>
					<tbody className='divide-y'>{query.data.items.map(asset => {
						const preview = asset.variants?.[0]?.url
						const canDelete = canMarkMediaForDeletion(asset, can('deleteMediaAsset'))
						return <tr key={asset.id} className='hover:bg-muted/30'>
							<td className='px-4 py-3'><div className='flex items-center gap-3'><AssetPreview asset={asset} /><div className='min-w-0'><p className='max-w-xs truncate font-medium'>{asset.filename}</p><p className='mt-0.5 text-xs text-muted-foreground'>{mediaKindLabel[asset.kind]} · v{asset.version}</p><p className='mt-0.5 max-w-xs truncate font-mono text-[10px] text-muted-foreground'>{asset.id}</p></div></div></td>
							<td className='px-4 py-3'><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${stateClass[asset.state]}`}>{mediaStateLabel[asset.state]}</span>{asset.rejection_message ? <p className='mt-2 max-w-48 text-xs text-red-700'>{asset.rejection_message}</p> : null}</td>
							<td className='px-4 py-3'>{formatBytes(asset.size_bytes)}</td>
							<td className='px-4 py-3'>{asset.variants?.length ?? 0}</td>
							<td className='px-4 py-3'>{asset.reference_count ?? 0}</td>
							<td className='px-4 py-3 text-muted-foreground'>{formatDate(asset.updated_at)}</td>
							<td className='px-4 py-3 text-right'><div className='inline-flex gap-1'><Button variant='ghost' size='icon' onClick={() => setDetailAsset(asset)} aria-label='Диагностика ассета'><Eye className='h-4 w-4' /></Button>{preview ? <Button variant='ghost' size='icon' asChild><a href={preview} target='_blank' rel='noreferrer' aria-label='Открыть вариант'><ExternalLink className='h-4 w-4' /></a></Button> : null}{canDelete ? <Button variant='ghost' size='icon' onClick={() => setDeleteAsset(asset)} aria-label='Пометить на удаление'><Trash2 className='h-4 w-4 text-red-600' /></Button> : asset.reference_count ? <span title='Удаление заблокировано активными ссылками' className='inline-flex h-9 w-9 items-center justify-center text-amber-700'><ShieldAlert className='h-4 w-4' /></span> : null}</div></td>
						</tr>
					})}</tbody>
				</table></div>
				<div className='flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between'><p className='text-sm text-muted-foreground' role='status' aria-live='polite'>Показано {query.data.items.length} из {query.data.page.total ?? query.data.items.length}</p><div className='flex gap-2'><Button variant='outline' size='sm' disabled={!cursorHistory.length || query.isFetching} onClick={goPrevious}><ChevronLeft className='h-4 w-4' /> Назад</Button><Button variant='outline' size='sm' disabled={!getNextCursor(query.data.page) || query.isFetching} onClick={goNext}>Далее <ChevronRight className='h-4 w-4' /></Button></div></div>
			</Card> : null}

			<MediaUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
			<Dialog open={Boolean(detailAsset)} onOpenChange={value => { if (!value) setDetailAsset(null) }}>
				<DialogContent className='max-h-[88vh] overflow-y-auto sm:max-w-3xl'>
					<DialogHeader>
						<DialogTitle>{detailAsset?.filename ?? 'Диагностика ассета'}</DialogTitle>
						<DialogDescription>Статус обработки, неизменяемые варианты, контроль версии и защита связей.</DialogDescription>
					</DialogHeader>
					{detailAsset ? <div className='space-y-5'>
						<div className='grid gap-3 rounded-xl border bg-muted/20 p-4 text-sm sm:grid-cols-2'>
							<div><span className='text-muted-foreground'>Asset ID</span><p className='mt-1 break-all font-mono text-xs'>{detailAsset.id}</p></div>
							<div><span className='text-muted-foreground'>State</span><p className='mt-1'>{mediaStateLabel[detailAsset.state]} · version {detailAsset.version}</p></div>
							<div><span className='text-muted-foreground'>ETag</span><p className='mt-1 break-all font-mono text-xs'>{detailAsset.etag}</p></div>
							<div><span className='text-muted-foreground'>Content</span><p className='mt-1'>{detailAsset.content_type ?? '—'} · {formatBytes(detailAsset.size_bytes)}</p></div>
							<div><span className='text-muted-foreground'>SHA-256</span><p className='mt-1 break-all font-mono text-xs'>{detailAsset.sha256 ?? '—'}</p></div>
							<div><span className='text-muted-foreground'>References</span><p className='mt-1'>{detailAsset.reference_count ?? 0}</p></div>
							<div><span className='text-muted-foreground'>Created</span><p className='mt-1'>{formatDate(detailAsset.created_at)}</p></div>
							<div><span className='text-muted-foreground'>Updated</span><p className='mt-1'>{formatDate(detailAsset.updated_at)}</p></div>
						</div>
						{detailAsset.rejection_code || detailAsset.rejection_message ? <div className='rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900'><p className='font-semibold'>{detailAsset.rejection_code ?? 'PROCESSING_REJECTED'}</p><p className='mt-1'>{detailAsset.rejection_message ?? 'Сервис обработки медиа отклонил файл'}</p></div> : null}
						<section className='space-y-3'><div className='flex items-center justify-between'><h3 className='font-semibold'>Варианты</h3><span className='text-xs text-muted-foreground'>{detailAsset.variants?.length ?? 0}</span></div>
							{detailAsset.variants?.length ? <div className='grid gap-3 sm:grid-cols-2'>{detailAsset.variants.map((variant, index) => <a key={`${variant.asset_id}-${variant.role}-${index}`} href={variant.url} target='_blank' rel='noreferrer' className='overflow-hidden rounded-xl border transition-colors hover:bg-muted/30'>
								{variant.url && detailAsset.kind !== 'VIDEO' ? <img src={variant.url} alt={variant.alt_text ?? variant.role} className='h-40 w-full bg-muted object-contain' /> : <div className='flex h-32 items-center justify-center bg-muted/40'><Film className='h-8 w-8 text-muted-foreground' /></div>}
								<div className='flex items-center justify-between gap-3 p-3 text-sm'><div><p className='font-medium'>{variant.role}</p><p className='text-xs text-muted-foreground'>{variant.state}</p></div><ExternalLink className='h-4 w-4' /></div>
							</a>)}</div> : <p className='rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground'>Worker ещё не создал варианты.</p>}
						</section>
					</div> : null}
				</DialogContent>
			</Dialog>
			<ConfirmDialog open={Boolean(deleteAsset)} onOpenChange={open => { if (!open && !deletePending) setDeleteAsset(null) }} title='Пометить ассет на удаление?' description='Сервис повторно проверит ссылки, сверит версию и запустит период отложенного удаления. Файл не удаляется немедленно.' confirmLabel='Запустить удаление' destructive isPending={deletePending} onConfirm={markDelete} />
		</section>
	)
}
